import { formatDateToDDMMMYYYY } from "../../../utils/dateUtils";
import { EnhancedTeamData } from "./GroupLeaderboardTable";
import { MatchSetupConfig } from "../../../models/match";

interface TeamCardProps {
  team: EnhancedTeamData;
  index: number;
  matchSetups: MatchSetupConfig[];
  isTopTeam: boolean;
}

const TeamCard = ({ team, index, matchSetups, isTopTeam }: TeamCardProps) => {

  return (
    <div
      className={`p-4 rounded-lg shadow-sm mb-3 cursor-pointer
        ${isTopTeam ? 'bg-green-50 dark:bg-green-900/20' : 'bg-white dark:bg-gray-800'}
        hover:shadow-md transition-shadow`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center">
          {team.teamLogo && (
            <img
              src={`/api/static/images/${team.teamLogo}`}
              alt={team.teamName}
              className="w-10 h-10 rounded-full mr-3"
            />
          )}
          <div>
            <div className="font-semibold text-gray-900 dark:text-gray-100">
              {team.teamName}
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Rank: {index + 1}
              {isTopTeam && (
                <span className="ml-2 text-green-600 dark:text-green-400">
                  {index === 0 ? '🏆' : '🥈'}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
          {team.totalScore}
        </div>
      </div>
      
      <div className="space-y-2">
        {matchSetups.map((match) => {
          const teamScore = team.matchScores.find(m => m.matchId === match.matchId);
          return (
            <div key={match.id} className="flex justify-between text-sm">
              <span className="text-gray-600 dark:text-gray-400">
                {match.courseName} - {formatDateToDDMMMYYYY(match.date ?? '')}
              </span>
              <span className="font-medium text-gray-900 dark:text-gray-100">
                {teamScore?.score || 0}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

interface GroupCardProps {
  groupName: string;
  teams: EnhancedTeamData[];
  matchSetups: MatchSetupConfig[];
}

const GroupCard = ({ groupName, teams, matchSetups }: GroupCardProps) => {
  const sortedTeams = [...teams].sort((a, b) => b.totalScore - a.totalScore);

  return (
    <div className="mb-4">
      <div className="bg-gray-100 dark:bg-gray-700 p-4 rounded-lg">
        <div className="flex items-center justify-between">
          <span className="font-bold text-gray-900 dark:text-gray-100">{groupName}</span>
          <span className="text-sm text-gray-600 dark:text-gray-400">
            {teams.length} teams
          </span>
        </div>
      </div>
      
      <div className="mt-3">
        {sortedTeams.map((team, index) => (
          <TeamCard
            key={team.teamId}
            team={team}
            index={index}
            matchSetups={matchSetups}
            isTopTeam={index < 2}
          />
        ))}
      </div>
    </div>
  );
};

export const GroupLeaderboardMobileView = ({
  groupedTeams,
  matchSetups,
}: {
  groupedTeams: Record<string, EnhancedTeamData[]>;
  matchSetups: MatchSetupConfig[];
}) => {
  if (Object.keys(groupedTeams).length === 0) {
    return (
      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
        No teams found in any groups
      </div>
    );
  }

  // Sort groups by name
  const sortedGroups = Object.entries(groupedTeams)
    .sort(([a], [b]) => a.localeCompare(b));

  return (
    <div className="p-4">
      {sortedGroups.map(([groupName, teams]) => (
        <GroupCard
          key={groupName}
          groupName={groupName}
          teams={teams}
          matchSetups={matchSetups}
        />
      ))}
    </div>
  );
};
